import { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import getFileType from '../../../../utils/getFileType';
import { i18n } from '../../../../language';
import { DocIcon } from '../../../../components/Icon';
import { onKeyUpEnterOrSpace, escapeHtmlKeepUrls } from '../../../../utils';
import ImgWithPreviewDialog from '../../../../components/ImgWithPreviewDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: '1.25rem',
    padding: '1.563rem 1.25rem',
    borderRadius: '0.625rem',
    backgroundColor: '#f5faff',
  },
  head: {
    display: 'flex',
    marginBottom: '0.625rem',
  },
  name: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    lineHeight: '1.313rem',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginRight: '1.5rem',
  },
  time: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    letterSpacing: '0.0218rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
  },
  body: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.625rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    whiteSpace: 'pre-line',
  },
  imgs: {
    width: '100%',
    margin: '1.25rem 0',
  },
  img: {
    width: '100%',
    height: 'auto',
    borderRadius: '0.625rem',
  },
  fileTitle: {
    marginTop: '1.875rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    letterSpacing: '0.35px',
    fontWeight: '500',
  },
  file: {
    height: '3.125rem',
    minWidth: '16.563rem',
    margin: '0.938rem 0.938rem 0 0',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
    backgroundColor: '#f5faff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.625rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    paddingRight: '1rem',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  box: {
    width: '1.875rem',
    height: '1.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 0.625rem',
  },
  docIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: theme.palette.primary.main,
  },
}));

const renderHexEmoji = (text) =>  {
   if (typeof text !== 'string') return text;

  // Enforces strict colon wrapping and exactly 8 hex characters
  const emojiRegex = /:([0-9a-fA-F]{8}):/g;

  return text.replace(emojiRegex, (match, hex) => {
    const matchedBytes = hex.match(/.{1,2}/g);
    if (!matchedBytes) return match;
    
    const codeArray = matchedBytes.map((byte) => parseInt(byte, 16));
    const byteArray = Uint8Array.from(codeArray);
    
    try {
      // 'fatal: true' forces an error on invalid UTF-8 sequences instead of outputting 
      const decoder = new TextDecoder('utf-8', { fatal: true });
      return decoder.decode(byteArray);
    } catch (error) {
      // Safely fall back to the original string if decoding fails
      return match; 
    }
  });
}

export default function Comment({ context, name, time, files, onHeightChange = () => {} }) {
  const classes = useStyles();

  const fileList = useMemo(() => {
    let list = [];

    list = _.map(Object.keys(files), (item) => {
      return files[item];
    });

    return list;
  }, [files]);

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <div className={classes.name}>{name}</div>
        <div className={classes.time}>{moment(time).format('YYYY-MM-DD HH:mm')}</div>
      </div>
      <div className={classes.body}>
        {context ? (
          <div
            className={classes.context}
            dangerouslySetInnerHTML={{
              __html: renderHexEmoji(escapeHtmlKeepUrls(context)),
            }}
          />
        ) : (
          ''
        )}
      </div>
      {fileList.length > 0 && (
        <div>
          <Grid className={classes.imgs} container spacing={3}>
            {fileList
              .filter((item) => {
                return getFileType(item.NAME) === 'picture';
              })
              .map((item, index) => {
                return (
                  <Grid item sm={6} lg={4} xl={3} key={item.DOWNLOAD_URL}>
                    <ImgWithPreviewDialog imgClassName={classes.img} url={item.DOWNLOAD_URL} onLoad={onHeightChange}/>
                  </Grid>
                );
              })}
          </Grid>
          {fileList.filter((item) => {
            return getFileType(item.NAME) !== 'picture';
          }).length > 0 && (
            <div
              style={{
                marginBottom: '1.25rem',
              }}
            >
              <div className={classes.fileTitle}>{`${i18n.t('EHANDOVER_NOTE_ATTACHMENT')} :`}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {fileList
                  .filter((item) => {
                    return getFileType(item.NAME) !== 'picture';
                  })
                  .map((item, index) => {
                    return (
                      <div
                        className={classes.file}
                        key={item.DOWNLOAD_URL}
                        onClick={() => window.open(item.DOWNLOAD_URL)}
                        tabIndex={0}
                        onKeyUp={(e) => onKeyUpEnterOrSpace(e, () => window.open(item.DOWNLOAD_URL))}
                      >
                        <div className={classes.box}>
                          <DocIcon className={classes.docIcon} />
                        </div>
                        {item.NAME}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
